<script lang="ts">
// eslint-disable-next-line @typescript-eslint/naming-convention
const Operator = {
  '=': 'equals',
  '≤': 'lessThanOrEqual',
  '≥': 'greaterThanOrEqual',
  '∈': 'inRange',
} as const;
type Operator = (typeof Operator)[keyof typeof Operator];

interface FilterModel {
  dateFrom: string | null;
  dateTo: string | null;
  type: Operator;
}

export interface LocalFilterModel {
  dateFrom: string | null;
  dateTo: string | null;
  type: Operator;
}
</script>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onBeforeMount, ref, shallowRef, watch } from 'vue';
import { IFloatingFilterParams } from 'ag-grid-enterprise';
import Wrapper from './FilterWrapper.vue';
import { isEqual } from 'lodash-es';

const localFilterModel = ref<LocalFilterModel>({
  dateFrom: null,
  dateTo: null,
  type: Operator['='],
});

const params = shallowRef<
  IFloatingFilterParams & { filterParams: { inputType: 'date' | 'datetime-local' | 'time' } }
>();

defineExpose({
  onParentModelChanged: updateLocalFilter,
});

onBeforeMount(() => {
  params.value = getCurrentInstance()?.proxy.$data.params;
  if (params.value === undefined) return;

  const filterModel = params.value.api.getColumnFilterModel<FilterModel>(params.value.column);
  updateLocalFilter(filterModel);

  params.value.column.addEventListener('widthChanged', () => updateColumnWidth(false));
});

function updateLocalFilter(filterModel: FilterModel | null) {
  filterModel ??= structuredClone(knownFilter);
  if (filterModel === null) {
    localFilterModel.value.type = Operator['='];
    localFilterModel.value.dateFrom = null;
    localFilterModel.value.dateTo = null;
  } else {
    localFilterModel.value = filterModel;
  }
}

const inputType = computed(() => {
  return params.value?.filterParams.inputType ?? 'date';
});

const elFilter = shallowRef<HTMLElement | null>(null);
watch(
  () => localFilterModel.value.type,
  () => updateColumnWidth(true),
  { immediate: true, deep: true }
);

async function updateColumnWidth(forceColumnResize: boolean) {
  await nextTick();
  if (!params.value || elFilter.value === null) return;

  const elHeaderCell = elFilter.value.closest('.ag-header-cell');
  // computedStyleMap is undefined in tests
  const headerStyle = elHeaderCell?.computedStyleMap?.();
  if (!headerStyle) return;

  const padding =
    ((headerStyle.get('padding-left') as CSSUnitValue | undefined)?.value ?? 0) +
    ((headerStyle.get('padding-right') as CSSUnitValue | undefined)?.value ?? 0);
  const widthNew = elFilter.value.getBoundingClientRect().width + padding;
  if (forceColumnResize) {
    params.value.api.autoSizeColumns([params.value.column], true);
    params.value.column.setActualWidth(widthNew, 'api');
  }
}

watch(
  () => localFilterModel.value.type,
  (operator) => {
    switch (operator) {
      case Operator['=']:
      case Operator['≤']:
      case Operator['≥']:
        localFilterModel.value.dateTo = null;
        break;
      case Operator['∈']:
        localFilterModel.value.dateTo = localFilterModel.value.dateFrom;
        break;
    }
  },
  { flush: 'sync' }
);

let knownFilter: LocalFilterModel | null = null;
watch(localFilterModel, applyFilter, { deep: true, immediate: true });

async function applyFilter(newFilter: LocalFilterModel) {
  if (isEqual(newFilter, knownFilter)) {
    // filter not changed, skipping, otherwise we get an endless loop,
    // because of the watch on localFilterModel, being triggered by onParentModelChanged
    return;
  }

  if (params.value === undefined) return;
  // input returns empty strings on clear, we need nulls instead, hence `|| null` below
  const filter = {
    dateFrom: newFilter.dateFrom || null,
    dateTo: newFilter.type === Operator['∈'] ? newFilter.dateTo || null : null,
    type: newFilter.type,
  };
  knownFilter = structuredClone(filter);
  await params.value.api.setColumnFilterModel<FilterModel>(
    params.value.column,
    filter.dateFrom === null || (filter.type === Operator['∈'] && filter.dateTo === null)
      ? null
      : filter
  );
  params.value.api.onFilterChanged();
}
</script>

<template>
  <Wrapper :data-test-filter="params?.column.getColId()">
    <div
      ref="elFilter"
      class="d-flex flex-grow-1"
      style="min-width: fit-content; max-width: max-content; padding-left: 4px"
    >
      <select
        v-model="localFilterModel.type"
        class="ag-input-field-input ag-text-field-input flex-grow-0"
        style="width: 20px; text-align: center"
      >
        <option v-for="(value, text) in Operator" :key="text" :value="value">{{ text }}</option>
      </select>
      <div
        class="flex-grow-1 d-flex align-center justify-end ag-input-field-input ag-text-field-input"
      >
        <input
          v-model="localFilterModel.dateFrom"
          autocomplete="off"
          class="ag-input-field-input input-date-time"
          :type="inputType"
        />
        <template v-if="localFilterModel.type === Operator['∈']">
          <span style="margin: 0 6px 0 4px">–</span>
          <input
            v-model="localFilterModel.dateTo"
            autocomplete="off"
            class="ag-input-field-input input-date-time"
            :type="inputType"
          />
        </template>
      </div>
    </div>
  </Wrapper>
</template>

<style lang="scss" scoped>
.input-date-time {
  --ag-borders-input: none;
  --ag-background-color: transparent;
  min-width: fit-content;
  max-width: max-content;
}
::-webkit-datetime-edit {
  max-width: max-content;
}
::-webkit-calendar-picker-indicator {
  padding-left: 0.5rem;
  margin: 0;
}
</style>
